import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { UserPlus, Search, MessageSquare, Gamepad2, Users, Sparkles, GraduationCap, Crown, Check } from 'lucide-react';

const LOGO_URL = "/logo.png";
const RACHEL_IMG = "https://customer-assets.emergentagent.com/job_game-gather-2/artifacts/af4aol4k_Rachel%20Mahjong.JPG";

const MISSION_ITEMS = [
  { n: 1, title: 'See all local Mahjong event opportunities in one place.', color: '#56c6df' },
  { n: 2, title: 'Find other people in your area looking to play.', color: '#56dfb4' },
  { n: 3, title: 'Locate local public places where you can meet up for a game.', color: '#f59e0b' },
  { n: 4, title: 'Find instructors close to you.', color: '#df5681' },
  { n: 5, title: 'Join a safe, moderated discussion board to talk all things Mahjong.', color: '#b456df' },
];

const VALUES = [
  { title: 'Community First', desc: 'This should be a welcoming home for every player, regardless of experience.', color: '#56dfb4' },
  { title: 'Safety', desc: 'Reporting and blocking tools, privacy toggles, and moderation keep the community safe.', color: '#df5681' },
  { title: 'Accessibility', desc: 'Players of all skill levels are welcome — from brand-new to decades of experience.', color: '#56c6df' },
  { title: 'Connection', desc: 'Bringing people together through this beautiful game is at the heart of everything here.', color: '#b456df' },
];

const FEATURES = [
  { title: 'Events Board', desc: 'Browse and host local Mahjong tournaments, meetups, open play, and classes. Filter by city or distance from your zip code, and pay securely for ticketed events.', color: '#56c6df', link: '/events', linkText: 'Browse Events →' },
  { title: 'Find Players Nearby', desc: 'Create a detailed profile and search for compatible players by skill level, availability, and distance. Send a Last-Minute Game Alert when you need a fourth right now.', color: '#56dfb4', link: '/find-players', linkText: 'Find Players →' },
  { title: 'Public Play Locations', desc: 'Discover public Mahjong games, clubs, and regular meetups across the country — synced daily from our community directory.', color: '#f59e0b', link: '/play-locations', linkText: 'View Locations →' },
  { title: 'Instructors', desc: 'Looking to learn or sharpen your skills? Connect with verified American Mahjong instructors in your area or online.', color: '#df5681', link: '/instructors', linkText: 'Meet Instructors →' },
  { title: 'Discussion Board', desc: 'Ask questions, share strategies, swap stories, and discover deals &amp; coupons in our dedicated community forum.', color: '#b456df', link: '/forum', linkText: 'Open the Forum →' },
  { title: 'Real-Time Messaging', desc: 'Chat directly with players you connect with to schedule games, share directions, and make new friends — all in one place.', color: '#64748b', link: '/messages', linkText: 'Go to Messages →' },
];const PLAYER_TIERS = [
  {
    id: 'open_play',
    name: 'Open Play',
    price: 'Free',
    icon: Users,
    color: '#64748b',
    tagline: 'Everyone starts here. Always free.',
    features: [
      'Create and edit your full profile',
      'View events and event details',
      'View instructor contact information',
      'Post events as needed ($0.99 free-to-attend · $9.99 paid)',
      'Search nearby public play locations',
      'Read & post on the Discussion Board',
    ],
  },
  {
    id: 'verified_player',
    name: 'Verified Player',
    price: '$0.99/mo or $9.99/yr',
    icon: Sparkles,
    color: '#56dfb4',
    tagline: 'Everything in Open Play, plus:',
    features: [
      'Search and connect with other players nearby',
      'In-site messaging with other members',
      'Email and optional SMS alerts for events near you',
      'Request a game with 1–3 other players (Last-Minute Game Alerts)',
    ],
  },
];

const INSTRUCTOR_TIERS = [
  {
    id: 'instructor_starter',
    name: 'Instructor Starter',
    price: 'Free',
    icon: GraduationCap,
    color: '#64748b',
    tagline: 'Free instructor membership — get listed and share events.',
    features: [
      'Create and edit your instructor profile',
      'View events and event details',
      'Post events as needed ($0.99 free-to-attend · $9.99 paid)',
      'Search nearby public play locations',
      'Read & post on the Discussion Board',
    ],
  },
  {
    id: 'instructor_plus',
    name: 'Instructor Advantage',
    price: '$9.99/mo or $99.99/yr',
    icon: Sparkles,
    color: '#56c6df',
    tagline: 'Everything in Instructor Starter, plus:',
    features: [
      'Search and connect with other players',
      'In-site messaging with members',
      'Email and optional SMS alerts',
      'Free-to-attend events are free to post',
      'Paid events discounted to $6.99 per event',
    ],
  },
  {
    id: 'instructor_unlimited',
    name: 'Instructor Unlimited',
    price: '$29.99/mo or $299.99/yr',
    icon: Crown,
    color: '#b456df',
    tagline: 'Everything in Instructor Advantage, plus:',
    features: [
      'Unlimited event posting — both free and paid events are free to post',
      'Premium Instructor badge displayed next to your name',
      'Priority placement in the instructor directory',
      'Best value for instructors posting 3+ paid events per month',
    ],
  },
];

const STEPS = [
  { num: 1, icon: UserPlus, title: 'Create Your Profile', desc: 'Sign up and share your Mahjong experience, availability, and preferences. The multi-step form makes it easy and takes only a few minutes.', color: '#56dfb4' },
  { num: 2, icon: Search, title: 'Find Local Players', desc: 'Use the search filters to find compatible players near you — filter by skill level, availability, and distance from your zip code.', color: '#56c6df' },
  { num: 3, icon: MessageSquare, title: 'Connect & Message', desc: 'Send a message to players you\'d like to play with, coordinate schedules, and arrange your first game.', color: '#df5681' },
  { num: 4, icon: Gamepad2, title: 'Play Mahjong!', desc: 'Meet up at your chosen location and enjoy a game of American Mahjong with your new group.', color: '#b456df' },
];

export default function AboutPage() {
  // HowTo JSON-LD for "Get Started in Four Simple Steps" → enables
  // Google's step-by-step rich snippet for queries like "how to find
  // mahjong players online". Schema spec:
  // https://schema.org/HowTo
  const howToJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Find Local Mahjong Players on Meet For Mahjong',
    description: 'Join the Meet For Mahjong community in four simple steps — create your profile, find local players, message and coordinate, then play.',
    totalTime: 'PT10M',
    step: STEPS.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.title,
      text: s.desc,
      url: `https://meetformahjong.com/about#step-${s.num}`,
    })),
  };

  return (
    <div data-testid="about-page" className="py-16 lg:py-24">
      <SEO
        title="About — Meet For Mahjong"
        description="Meet For Mahjong is a community platform created by Rachel to bring local American Mahjong events, players, instructors, play locations, and a safe discussion board together in one place."
        path="/about"
        jsonLd={howToJsonLd}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#df5681]">About</span>

        <div className="flex justify-center my-10">
          <img src={LOGO_URL} alt="Meet For Mahjong" className="w-64 h-auto drop-shadow-xl" />
        </div>

        <div className="prose max-w-none space-y-6 text-slate-600 leading-relaxed">
          {/* 1. Mission */}
          <h2 className="text-2xl font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Meet For Mahjong's Mission
          </h2>
          <p>
            Meet For Mahjong is built to be the <strong>one place</strong> an American Mahjong player can go to do everything that matters most. No more piecing things together across Facebook groups, Instagram posts, group texts, and scattered websites. Here you can:
          </p>
          <ol className="not-prose space-y-3 pl-0 list-none">
            {MISSION_ITEMS.map(item => (
              <li key={item.n} className="flex items-start gap-4 p-4 rounded-2xl border border-slate-100 bg-white" data-testid={`about-mission-${item.n}`}>
                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: item.color }}>
                  {item.n}
                </div>
                <p className="text-slate-700 m-0">{item.title}</p>
              </li>
            ))}
          </ol>

          {/* 2. Meet the Player */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Meet the Player</h2>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-6 items-start">
            <div className="sm:col-span-1">
              <img
                src={RACHEL_IMG}
                alt="Rachel holding her American Mahjong rack"
                loading="lazy"
                className="w-full aspect-square object-cover rounded-2xl shadow-lg"
                data-testid="about-rachel-photo"
              />
              <p className="text-xs text-slate-400 mt-2 italic text-center">Rachel — founder, Meet For Mahjong</p>
            </div>
            <div className="sm:col-span-2 space-y-4 text-slate-600 leading-relaxed">
              <p>
                I'm Rachel, and I absolutely love playing American Mahjong—it's one of my favorite ways to connect, unwind, and have fun. I'm based in Atlanta, where I live with my husband (who I met volunteering through the University of Georgia), our two kids, and our three pups. When I'm not playing mahjong, I'm usually spending time with family and friends, hosting book club, leading my daughter's Girl Scout troop, or cheering on Georgia football (Go Dawgs!).
              </p>
            </div>
          </div>

          {/* 3. Why I Built This */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Why I Built This</h2>
          <p>
            I started this website because my mom fell in love with the game but couldn't always find people to play with. The idea started because I wanted her to have a safe and easy way to connect with others in her community while playing Mahjong.
          </p>
          <p>
            Finding other Mahjong players can be challenging, especially if you've recently moved to a new area or are just learning the game. Meet For Mahjong solves this by giving the American Mahjong community a single, dedicated home.
          </p>

          {/* 4. Values */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Meet For Mahjong Values</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose">
            {VALUES.map(v => (
              <div key={v.title} className="p-5 rounded-2xl border border-slate-100 bg-white" data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-3 h-3 rounded-full mb-3" style={{ backgroundColor: v.color }} />
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.title}</h3>
                <p className="text-sm text-slate-500">{v.desc}</p>
              </div>
            ))}
          </div>

          {/* 5. What You Can Do Here */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>What You Can Do Here</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose">
            {FEATURES.map(feature => (
              <div key={feature.title} className="p-5 rounded-2xl border border-slate-100 bg-white hover:shadow-md transition-all duration-300" data-testid={`about-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-3 h-3 rounded-full mb-3" style={{ backgroundColor: feature.color }} />
                <h3 className="font-semibold mb-1 text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h3>
                <p className="text-sm text-slate-500 mb-3" dangerouslySetInnerHTML={{ __html: feature.desc }} />
                <Link to={feature.link} className="text-xs font-semibold" style={{ color: feature.color }}>
                  {feature.linkText}
                </Link>
              </div>
            ))}
          </div>

          {/* 6. Get Started in Four Simple Steps */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Get Started in Four Simple Steps</h2>
          <div className="not-prose space-y-6">
            {STEPS.map((step, i) => (
              <div key={step.num} id={`step-${step.num}`} className={`flex flex-col md:flex-row gap-5 items-start ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-testid={`about-step-${step.num}`}>
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center relative" style={{ backgroundColor: `${step.color}15` }}>
                    <step.icon className="w-6 h-6" style={{ color: step.color }} />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#0f172a] text-white text-xs font-bold flex items-center justify-center">
                      {step.num}
                    </span>
                  </div>
                </div>
                <div className="flex-1 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
                  <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
            <div className="text-center pt-4">
              <Link to="/register" className="inline-block px-8 py-3 rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] transition-colors font-medium" data-testid="about-get-started-btn">
                Get Started — It's Free
              </Link>
            </div>
          </div>

          {/* 7. How Memberships Work */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>How Memberships Work</h2>
          <p>
            Meet For Mahjong has two membership paths — one for <strong>players</strong> and one for <strong>instructors</strong>.
            Both start with a free tier that already includes a lot. Paid tiers add features on top of the free tier — they don't replace it.
          </p>

          {/* Player path */}
          <h3 className="text-lg font-semibold text-[#0f172a] pt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            For Players
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose" data-testid="about-player-tiers">
            {PLAYER_TIERS.map(t => (
              <div
                key={t.id}
                className="p-5 rounded-2xl border border-slate-100 bg-white"
                data-testid={`about-tier-${t.id}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${t.color}15` }}>
                    <t.icon className="w-5 h-5" style={{ color: t.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#0f172a] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name}</h4>
                    <p className="text-xs font-bold" style={{ color: t.color }}>{t.price}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 italic mb-3">{t.tagline}</p>
                <ul className="space-y-1.5">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Instructor path */}
          <h3 className="text-lg font-semibold text-[#0f172a] pt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            For Instructors
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 not-prose" data-testid="about-instructor-tiers">
            {INSTRUCTOR_TIERS.map(t => (
              <div
                key={t.id}
                className="p-5 rounded-2xl border border-slate-100 bg-white"
                data-testid={`about-tier-${t.id}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${t.color}15` }}>
                    <t.icon className="w-5 h-5" style={{ color: t.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#0f172a] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name}</h4>
                    <p className="text-xs font-bold" style={{ color: t.color }}>{t.price}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 italic mb-3">{t.tagline}</p>
                <ul className="space-y-1.5">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-sm text-slate-500 italic">
            See the full breakdown — including event posting fees and the Premium Instructor badge — on the{' '}
            <Link to="/pricing" className="text-[#56dfb4] font-semibold hover:underline">Membership page</Link>.
          </p>

          {/* 8. About American Mahjong */}
          <h2 className="text-2xl font-semibold text-[#0f172a] pt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>About American Mahjong</h2>
          <p>
            American Mahjong, also known as Mahjong (Mah Jongg), is a tile-based game that was adapted from the Chinese game in the 1920s. It's played with a set of 152 tiles and uses a unique card from the National Mah Jongg League (NMJL) that changes each year.
          </p>
          <p>
            The game combines skill, strategy, and a little bit of luck. It's typically played with four players and is popular across the United States, especially among social groups and community centers.
          </p>
        </div>
      </div>
    </div>
  );
}
